import React from 'react';
import { css as reactCss } from '@emotion/react';

import { Price } from '~api/api';

import { useGlobalState } from '~src/main/context/global-state';
import { useTranslate } from '~src/main/context/localization';

import { createCurrencyFormatFn } from './currency-format';
import { getPriceRange, priceRangeToString } from './price-range';

/**
 * "formatPrice" function with global flags included.
 */
export function useFormatPriceFn() {
  const globalState = useGlobalState();

  return createCurrencyFormatFn({
    defaultCurrency: globalState.currency,
    displayIsoCode: globalState.flags.display_currency_iso_code as unknown as boolean,
    placeCurrencyBefore: globalState.flags.show_currency_before_price as unknown as boolean,
  });
}

interface PriceComponentProps {
  prices: Price[];
}

/**
 * Price component has no styling, it is suitable to take any font styling from
 * parent.
 */
export const PriceComponent: React.FC<PriceComponentProps> = ({ prices, ...props }) => {
  const tICU = useTranslate();
  const globalState = useGlobalState();
  const formatFn = useFormatPriceFn();

  if (prices.length === 0) {
    return null;
  }

  const range = getPriceRange(prices);

  const priceText =
    range.max === 0 && !globalState.flags.enable_price_zero_value
      ? tICU('widget.PRICE_FREE')
      : formatFn(priceRangeToString(range));

  return (
    <span data-test="price" css={styles.regular} {...props}>
      {priceText}
    </span>
  );
};

const styles = {
  regular: reactCss({
    fontVariationSettings: '"wght" 500',
  }),
};
